import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    X, Send, Sparkles, Bot, User, Bell, AlertTriangle, Pin,
    Save, Clock, Loader, ChevronRight, ChevronLeft, Lightbulb, RefreshCw,
    Settings, Users, Calendar, Paperclip, Check, Upload, Globe, File,
    Link as LinkIcon, Eye, Trash2, ExternalLink, ChevronDown, ChevronUp, Brain, Loader2,
    PanelLeftClose, PanelLeftOpen
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import RichTextEditor from '../RichTextEditor';
import AIFileUpload from '../Tasks/AIFileUpload';
import AssignmentPlaceholderPicker from '../Courses/AssignmentPlaceholderPicker';

// Web Search Results Display Component
const WebSearchResultsDisplay = ({ results }) => {
    const [expandedIndex, setExpandedIndex] = useState(null);

    if (!results || results.length === 0) return null;

    return (
        <div className="mt-3 pt-3 border-t border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-medium text-slate-400">Web Sources</span>
            </div>
            <div className="space-y-2">
                {results.map((result, index) => (
                    <div
                        key={index}
                        className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden"
                    >
                        <div className="px-3 py-2">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <a
                                        href={result.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-blue-400 hover:text-blue-300 truncate block"
                                    >
                                        {result.title}
                                    </a>
                                    <span className="text-xs text-slate-500">{result.domain}</span>
                                </div>
                                <a
                                    href={result.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-slate-500 hover:text-blue-400 transition-colors flex-shrink-0"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            </div>
                            <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                                {result.snippet}
                            </p>
                        </div>
                        {result.fullContent && (
                            <>
                                {expandedIndex === index && (
                                    <div className="px-3 pb-2">
                                        <div className="text-xs text-slate-300 bg-slate-900/50 p-2 rounded max-h-40 overflow-y-auto">
                                            {result.fullContent}
                                        </div>
                                    </div>
                                )}
                                <button
                                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                                    className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-slate-400 hover:text-slate-300 bg-slate-800/60 hover:bg-slate-700/50 transition-colors"
                                >
                                    {expandedIndex === index ? (
                                        <>
                                            <ChevronUp className="w-3 h-3" />
                                            <span>Show Less</span>
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown className="w-3 h-3" />
                                            <span>View More</span>
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const AINoticeGenerator = ({ isOpen, onClose, onSuccess, phase, editNotice = null, initialMessages = null, prefilledData = null }) => {
    // Determine if we're in edit mode or reminder mode
    const isEditMode = editNotice !== null && editNotice?.id;
    const isReminderMode = prefilledData?.reminderMode === true;

    // Chat State
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const chatEndRef = useRef(null);
    const inputRef = useRef(null);

    // Mobile State
    const [mobileActivePanel, setMobileActivePanel] = useState('chat');

    // Chat panel collapse state (desktop only)
    const [isChatCollapsed, setIsChatCollapsed] = useState(() => {
        const saved = localStorage.getItem('ai_notice_generator_chat_collapsed');
        return saved === 'true';
    });

    const toggleChatCollapsed = useCallback(() => {
        setIsChatCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('ai_notice_generator_chat_collapsed', String(next));
            return next;
        });
    }, []);

    // Split Panel State
    const [splitRatio, setSplitRatio] = useState(() => {
        const saved = localStorage.getItem('ai_notice_generator_split_ratio');
        return saved ? parseFloat(saved) : 40;
    });
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef(null);
    const MIN_CHAT_WIDTH = 300;
    const MIN_EDITOR_WIDTH = 400;

    // Streaming State
    const [streamingMessage, setStreamingMessage] = useState('');
    const [streamingPhase, setStreamingPhase] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);

    // Notice Editor State
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        priority: 'normal',
        phase: phase || 'onboarding',
        assignment_type: 'all',
        assigned_roles: [],
        assigned_employee_ids: [],
        assignment_placeholders: [],
        show_popup_on_login: false,
        external_link: '',
        expiry_date: '',
        scheduled_date: '',
        attachments: [],
        is_ai_generated: true,
        ai_chat_history: []
    });

    const [isSaving, setIsSaving] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [activeEditorTab, setActiveEditorTab] = useState('content');

    // Suggestions
    const [suggestions, setSuggestions] = useState([]);

    // AI File Upload State
    const [aiUploadedFiles, setAiUploadedFiles] = useState([]);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [fileUploadProgress, setFileUploadProgress] = useState(0);
    const [isWebSearchEnabled, setIsWebSearchEnabled] = useState(false);
    const [isWebSearching, setIsWebSearching] = useState(false);
    const [currentWebSearchResults, setCurrentWebSearchResults] = useState(null);
    const webSearchResultsRef = useRef(null);
    // Files awaiting user intent choice (attach vs analyze)
    const [pendingIntentFiles, setPendingIntentFiles] = useState([]);
    const [fileProcessingMessage, setFileProcessingMessage] = useState(null);
    const aiFileInputRef = useRef(null);

    // Track unsaved changes
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const initialStateRef = useRef(null);

    // Check if editor has content (not blank)
    const hasEditorContent = () => {
        return (
            (formData.title && formData.title.trim() !== '') ||
            (formData.content && formData.content.trim() !== '') ||
            formData.show_popup_on_login !== false ||
            formData.priority !== 'normal'
        );
    };

    // Initialize session and welcome message
    useEffect(() => {
        if (isOpen) {
            const newSessionId = `notice_session_${Date.now()}`;
            setSessionId(newSessionId);

            if (isEditMode) {
                setFormData({
                    title: editNotice.title || '',
                    content: editNotice.content || '',
                    priority: editNotice.priority || 'normal',
                    phase: editNotice.phase || phase || 'onboarding',
                    assignment_type: editNotice.assignment_type || 'all',
                    assigned_roles: editNotice.assigned_roles || [],
                    assigned_employee_ids: editNotice.assigned_employee_ids || [],
                    assignment_placeholders: editNotice.assignment_placeholders || [],
                    show_popup_on_login: editNotice.show_popup_on_login || false,
                    external_link: editNotice.external_link || '',
                    expiry_date: editNotice.expiry_date ? editNotice.expiry_date.split('T')[0] : '',
                    scheduled_date: editNotice.scheduled_date ? editNotice.scheduled_date.split('T')[0] : '',
                    attachments: editNotice.attachments || [],
                    is_ai_generated: true,
                    ai_chat_history: editNotice.ai_chat_history || []
                });

                if (initialMessages && Array.isArray(initialMessages) && initialMessages.length > 0) {
                    setMessages(initialMessages);
                    setMessages(prev => [...prev, {
                        id: Date.now(),
                        role: 'assistant',
                        content: `👋 Welcome back! I see you're editing **"${editNotice.title}"**. What changes would you like to make?`,
                        timestamp: new Date()
                    }]);
                } else {
                    setMessages([{
                        id: Date.now(),
                        role: 'assistant',
                        content: `👋 You're editing the notice **"${editNotice.title}"**. I can help you update the content, change the priority, or modify any other settings. What would you like to change?`,
                        timestamp: new Date()
                    }]);
                }

                setSuggestions([
                    'Make the tone more professional',
                    'Add more details',
                    'Change the priority to urgent',
                    'Simplify the content'
                ]);
            } else {
                // Fresh session
                // Helper to build context string for the AI from prefilled data
                const buildPrefilledContextString = (data) => {
                    if (!data || Object.keys(data).length === 0) return null;
                    const parts = [];
                    if (data.reminderMode) {
                        parts.push(`This is a REMINDER notice for the activity: "${data.activityTitle || 'Untitled Activity'}"`);
                        parts.push(`Activity Type: ${data.activityType || 'task'}`);
                        if (data.recipientCount) parts.push(`${data.recipientCount} employees haven't completed this activity yet`);
                        if (data.reminderNumber) parts.push(`This will be reminder #${data.reminderNumber}`);
                        if (data.overdueCount) parts.push(`${data.overdueCount} are overdue`);
                        if (data.stalledCount) parts.push(`${data.stalledCount} are stalled`);
                        return `Create a reminder notice for employees who haven't completed the ${data.activityType || 'activity'}: "${data.activityTitle}".\n${parts.join('\n')}`;
                    }
                    if (data.title) parts.push(`Title: "${data.title}"`);
                    if (data.description) parts.push(`Description: ${data.description}`);
                    if (data.category) parts.push(`Category: ${data.category}`);
                    if (data.priority) parts.push(`Priority: ${data.priority}`);
                    return parts.length > 0 ? `Please help me create this notice/announcement:\n${parts.join('\n')}` : null;
                };

                const prefilledMessageContext = buildPrefilledContextString(prefilledData);

                const initialChatMessages = [{
                    id: 1,
                    role: 'assistant',
                    content: isReminderMode
                        ? `🔔 **Reminder Mode** — I'll help you draft a reminder for **"${prefilledData.activityTitle}"**.\n\n${prefilledData.recipientCount || 0} employee${(prefilledData.recipientCount || 0) !== 1 ? 's haven\'t' : ' hasn\'t'} completed this ${prefilledData.activityType || 'activity'} yet${prefilledData.overdueCount ? ` (${prefilledData.overdueCount} overdue)` : ''}.${prefilledData.reminderNumber > 1 ? ` This will be reminder #${prefilledData.reminderNumber}.` : ''}\n\nI'll generate a contextual reminder. You can refine it before sending.`
                        : `👋 Hello! I'm your AI Notice Assistant. I can help you create professional **company notices and announcements** for your ${phase} process.\n\nTell me what you'd like to announce. For example:\n- "Create a welcome notice for new employees"\n- "Write an urgent announcement about office closure"\n- "Draft a policy update notice"`,
                    timestamp: new Date()
                }];

                if (prefilledMessageContext) {
                    initialChatMessages.push({
                        id: 2,
                        role: 'user',
                        content: prefilledMessageContext,
                        timestamp: new Date()
                    });
                    initialChatMessages.push({
                        id: 3,
                        role: 'assistant',
                        content: "I've loaded the details you requested. I can refine this notice for you, or we can just save it as is. Would you like me to expand on the content or change the tone?",
                        timestamp: new Date()
                    });
                }

                setMessages(initialChatMessages);

                // Reset formData for fresh session
                setFormData({
                    title: prefilledData?.title || '',
                    content: prefilledData?.description || prefilledData?.content || '',
                    priority: prefilledData?.priority || 'normal',
                    phase: prefilledData?.phase || phase || 'onboarding',
                    assignment_type: isReminderMode ? 'specific' : 'all',
                    assigned_roles: [],
                    assigned_employee_ids: isReminderMode && prefilledData?.recipientIds ? prefilledData.recipientIds : [],
                    assignment_placeholders: [],
                    show_popup_on_login: isReminderMode ? true : false,
                    external_link: '',
                    expiry_date: '',
                    scheduled_date: '',
                    attachments: [],
                    is_ai_generated: true,
                    ai_chat_history: [],
                    // Reminder metadata
                    is_reminder: isReminderMode || false,
                    source_activity_id: isReminderMode ? prefilledData?.activityId : null,
                    source_activity_type: isReminderMode ? prefilledData?.activityType : null,
                    reminder_metadata: isReminderMode ? JSON.stringify({
                        reminder_number: prefilledData?.reminderNumber || 1,
                        activity_title: prefilledData?.activityTitle || ''
                    }) : null
                });

                setSuggestions(isReminderMode ? [
                    'Generate draft reminder',
                    'Make it friendly but firm',
                    'Include a deadline warning',
                    'Add encouragement'
                ] : prefilledData ? [
                    'Make the tone more professional',
                    'Make it more friendly',
                    'Change priority to urgent'
                ] : [
                    `Create a welcome announcement`,
                    `Write a policy update notice`,
                    `Draft a team update`,
                    `Create an event announcement`
                ]);
            }

            fetchRoles();
            fetchEmployees();
        }
    }, [isOpen, phase, isEditMode, editNotice, initialMessages]);

    // Detect changes to form data
    useEffect(() => {
        if (isOpen && initialStateRef.current !== null) {
            const currentState = JSON.stringify({ formData, messages: messages.length });
            const hasChanges = currentState !== initialStateRef.current;
            setHasUnsavedChanges(hasChanges && hasEditorContent());
        }
    }, [formData, messages, isOpen]);

    // Set initial state reference
    useEffect(() => {
        if (isOpen && initialStateRef.current === null) {
            setTimeout(() => {
                initialStateRef.current = JSON.stringify({ formData, messages: messages.length });
                setHasUnsavedChanges(false);
            }, 500);
        }
    }, [isOpen]);



    // Handle browser back button - show unsaved changes modal
    useEffect(() => {
        if (!isOpen) return;

        const handlePopState = () => {
            if (isOpen && hasUnsavedChanges && hasEditorContent() && !showUnsavedModal) {
                window.history.pushState(null, '', window.location.href);
                setShowUnsavedModal(true);
            }
        };

        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen, hasUnsavedChanges, showUnsavedModal]);

    // Auto-scroll chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamingMessage]);

    // Focus input on open
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Lock body scroll
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${window.scrollY}px`;
        } else {
            const scrollY = document.body.style.top;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
        };
    }, [isOpen]);

    // Drag handlers for resizable split panel
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const mouseX = e.clientX - containerRect.left;

        let newRatio = (mouseX / containerWidth) * 100;
        const minChatRatio = (MIN_CHAT_WIDTH / containerWidth) * 100;
        const maxChatRatio = 100 - (MIN_EDITOR_WIDTH / containerWidth) * 100;

        newRatio = Math.max(minChatRatio, Math.min(maxChatRatio, newRatio));
        setSplitRatio(newRatio);
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            localStorage.setItem('ai_notice_generator_split_ratio', splitRatio.toString());
        }
    }, [isDragging, splitRatio]);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const fetchRoles = async () => {
        try {
            const response = await api.get('/notices/company-roles');
            setAvailableRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const fetchEmployees = async () => {
        try {
            const response = await api.get('/notices/company-employees');
            setEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    // Streaming message handler
    const handleSendMessageWithStreaming = async (messageToSend, currentFiles) => {
        setIsStreaming(true);
        setStreamingMessage('');
        setStreamingPhase('Analyzing your request...');

        const token = localStorage.getItem('token');
        const baseURL = api.defaults.baseURL;

        try {
            const response = await fetch(`${baseURL}/notices/generate-ai-chat-stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: messageToSend,
                    session_id: sessionId,
                    phase: formData.phase,
                    conversation_history: messages.slice(-10).map(m => ({
                        role: m.role,
                        content: m.content
                    })),
                    current_notice: {
                        title: formData.title,
                        content: formData.content,
                        priority: formData.priority
                    },
                    uploaded_files: currentFiles,
                    web_search_enabled: isWebSearchEnabled
                })
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));

                            if (data.type === 'phase') {
                                setStreamingPhase(data.phase);
                                if (data.phase?.includes('Searching for current information')) {
                                    setIsWebSearching(true);
                                }
                            } else if (data.type === 'web_search_results') {
                                webSearchResultsRef.current = data.results;
                                setCurrentWebSearchResults(data.results);
                                setIsWebSearching(false);
                            } else if (data.type === 'content') {
                                // Try to extract reply for chat display
                                const replyMatch = buffer.match(/"reply"\s*:\s*"((?:[^"\\]|\\.)*)(?:"|$)/);
                                if (replyMatch && replyMatch[1]) {
                                    try {
                                        const extractedReply = replyMatch[1]
                                            .replace(/\\n/g, '\n')
                                            .replace(/\\"/g, '"')
                                            .replace(/\\\\/g, '\\');
                                        setStreamingMessage(extractedReply);
                                    } catch {
                                        setStreamingMessage(replyMatch[1]);
                                    }
                                }
                            } else if (data.type === 'complete') {
                                // Add AI's reply to chat
                                const aiMessage = {
                                    id: Date.now() + 1,
                                    role: 'assistant',
                                    content: data.reply,
                                    timestamp: new Date(),
                                    webSearchResults: webSearchResultsRef.current || null,
                                    action: data.action
                                };
                                setMessages(prev => [...prev, aiMessage]);
                                setCurrentWebSearchResults(null);
                                webSearchResultsRef.current = null;

                                // Update notice editor
                                if (data.notice_update) {
                                    setFormData(prev => ({
                                        ...prev,
                                        title: data.notice_update.title || prev.title,
                                        content: data.notice_update.content || prev.content,
                                        priority: data.notice_update.priority || prev.priority,
                                        show_popup_on_login: data.notice_update.show_popup_on_login ?? prev.show_popup_on_login,
                                        external_link: data.notice_update.external_link || prev.external_link,
                                        assignment_type: data.notice_update.assignment_type || prev.assignment_type,
                                        assignment_placeholders: data.notice_update.assignment_placeholders || prev.assignment_placeholders
                                    }));

                                    if (data.notice_update.title || data.notice_update.content) {
                                        toast.success('Notice updated! Check the editor on the right.');
                                    }
                                }

                                // Handle file actions
                                if (data.file_action) {
                                    if (data.file_action.type === 'attach') {
                                        let filesToAttach = aiUploadedFiles;

                                        if (data.file_action.files && Array.isArray(data.file_action.files) && data.file_action.files.length > 0) {
                                            const fileNamesToAttach = data.file_action.files.map(f =>
                                                typeof f === 'string' ? f : f.file_name
                                            );
                                            filesToAttach = aiUploadedFiles.filter(f =>
                                                fileNamesToAttach.includes(f.file_name)
                                            );
                                        }

                                        if (filesToAttach.length > 0) {
                                            setFormData(prev => {
                                                const existingAttachments = prev.attachments || [];
                                                const newToAttach = filesToAttach.filter(f =>
                                                    !existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
                                                );

                                                if (newToAttach.length > 0) {
                                                    const attachmentFiles = newToAttach.map(f => ({
                                                        file_name: f.file_name,
                                                        file_url: f.file_url,
                                                        file_key: f.file_key,
                                                        file_size: f.file_size,
                                                        file_type: f.file_type
                                                    }));
                                                    toast.success(`📎 Attached: ${newToAttach.map(f => f.file_name).join(', ')}`);
                                                    return {
                                                        ...prev,
                                                        attachments: [...existingAttachments, ...attachmentFiles]
                                                    };
                                                }
                                                return prev;
                                            });
                                        }
                                    } else if (data.file_action.type === 'remove') {
                                        if (data.file_action.files && Array.isArray(data.file_action.files) && data.file_action.files.length > 0) {
                                            const fileNamesToRemove = data.file_action.files.map(f =>
                                                typeof f === 'string' ? f : f.file_name
                                            );
                                            setFormData(prev => ({
                                                ...prev,
                                                attachments: (prev.attachments || []).filter(a =>
                                                    !fileNamesToRemove.includes(a.file_name)
                                                )
                                            }));
                                            toast.success(`🗑️ Removed: ${fileNamesToRemove.join(', ')}`);
                                        }
                                    }
                                }

                                // Update suggestions
                                if (data.suggestions && data.suggestions.length > 0) {
                                    setSuggestions(data.suggestions);
                                }
                            } else if (data.type === 'error') {
                                toast.error(data.message || 'An error occurred');
                            }
                        } catch (e) {
                            // Skip invalid JSON
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Streaming error:', error);
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: "I apologize, but I encountered an error. Please try again.",
                timestamp: new Date(),
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsStreaming(false);
            setStreamingMessage('');
            setStreamingPhase(null);
        }
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isGenerating) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: inputMessage,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        const messageToSend = inputMessage;
        setInputMessage('');
        setIsGenerating(true);

        try {
            await handleSendMessageWithStreaming(messageToSend, aiUploadedFiles);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setInputMessage(suggestion);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleContentChange = (content) => {
        setFormData(prev => ({ ...prev, content }));
    };

    const handleRoleToggle = (role) => {
        setFormData(prev => ({
            ...prev,
            assigned_roles: prev.assigned_roles.includes(role)
                ? prev.assigned_roles.filter(r => r !== role)
                : [...prev.assigned_roles, role]
        }));
    };

    const handleEmployeeToggle = (employeeId) => {
        setFormData(prev => ({
            ...prev,
            assigned_employee_ids: prev.assigned_employee_ids.includes(employeeId)
                ? prev.assigned_employee_ids.filter(id => id !== employeeId)
                : [...prev.assigned_employee_ids, employeeId]
        }));
    };

    const handleRemoveAttachment = (index) => {
        setFormData(prev => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index)
        }));
    };

    // Handle AI file uploads - DEFERRED PROCESSING
    const handleAiFilesUploaded = (newFiles) => {
        // Check if files need intent choice (deferred processing)
        const filesNeedingIntent = newFiles.filter(f => f.needs_intent && !f.error);

        if (filesNeedingIntent.length > 0) {
            // Store files awaiting intent choice
            setPendingIntentFiles(prev => [...prev, ...filesNeedingIntent]);
            // Add to uploaded files list
            setAiUploadedFiles(prev => [...prev, ...newFiles.filter(f => !f.error)]);

            // Add message with intent chooser
            const fileNames = filesNeedingIntent.map(f => f.file_name).join(', ');
            const intentMessage = {
                id: Date.now(),
                role: 'assistant',
                content: filesNeedingIntent.length === 1
                    ? `📄 Uploaded **"${fileNames}"**. What would you like to do with this file?`
                    : `📄 Uploaded ${filesNeedingIntent.length} files. What would you like to do with them?`,
                timestamp: new Date(),
                action: 'file_intent_choice',
                pendingFiles: filesNeedingIntent
            };
            setMessages(prev => [...prev, intentMessage]);
            return;
        }

        // Handle already-processed files (legacy flow)
        setAiUploadedFiles(prev => [...prev, ...newFiles.filter(f => !f.error)]);

        const completedFiles = newFiles.filter(f => f.embedding_status === 'completed');
        if (completedFiles.length > 0) {
            const fileNames = completedFiles.map(f => f.file_name).join(', ');
            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `📄 I've read **"${fileNames}"**. What would you like me to do with it?`,
                timestamp: new Date(),
                action: 'file_processed'
            };
            setMessages(prev => [...prev, confirmMessage]);
            setSuggestions([
                'Summarize this document',
                'Create a notice from this content',
                'Add this as an attachment',
                'What are the key points?'
            ]);
        }
    };

    // Direct file upload handler for inline file picker (Claude/Gemini style)
    const handleDirectFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Validate file count
        if (aiUploadedFiles.length + files.length > 5) {
            toast.error('Maximum 5 files allowed per session');
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
            return;
        }

        // Validate file sizes (max 10MB each)
        const invalidFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error(`Files exceed 10MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
            return;
        }

        setIsFileUploading(true);
        setFileUploadProgress(0);

        try {
            const formDataUpload = new FormData();
            files.forEach(file => {
                formDataUpload.append('files', file);
            });
            formDataUpload.append('session_id', sessionId);

            const response = await api.post('/files/ai-upload', formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setFileUploadProgress(percentCompleted);
                },
            });

            if (response.data.success) {
                const newFiles = response.data.files.filter(f => !f.error);
                handleAiFilesUploaded(newFiles);
                const fileNames = newFiles.map(f => f.file_name).join(', ');
                toast.success(
                    newFiles.length === 1
                        ? `Uploaded: ${fileNames}`
                        : `${newFiles.length} files uploaded`
                );
            }
        } catch (error) {
            console.error('AI file upload error:', error);
            toast.error(error.response?.data?.message || 'Failed to upload files');
        } finally {
            setIsFileUploading(false);
            setFileUploadProgress(0);
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
        }
    };

    // Handle "Attach to Notice" intent choice
    const handleAttachToNotice = (files) => {
        // Check for already attached files
        const existingAttachments = formData.attachments || [];
        const alreadyAttached = files.filter(f =>
            existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
        );
        const newToAttach = files.filter(f =>
            !existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
        );

        // Notify about already attached files
        if (alreadyAttached.length > 0) {
            const alreadyNames = alreadyAttached.map(f => f.file_name).join(', ');
            toast(`📎 Already attached: ${alreadyNames}`, { icon: 'ℹ️' });

            if (newToAttach.length === 0) {
                // All files already attached, add message and return
                const message = {
                    id: Date.now(),
                    role: 'assistant',
                    content: `📎 **${alreadyNames}** is already attached to this notice.`,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, message]);

                // Remove from pending files
                const fileKeys = files.map(f => f.file_key);
                setPendingIntentFiles(prev => prev.filter(f => !fileKeys.includes(f.file_key)));
                return;
            }
        }

        // Add only new files to notice attachments
        if (newToAttach.length > 0) {
            const attachments = newToAttach.map(f => ({
                file_name: f.file_name,
                file_url: f.file_url,
                file_key: f.file_key,
                file_size: f.file_size,
                file_type: f.file_type
            }));

            setFormData(prev => ({
                ...prev,
                attachments: [...(prev.attachments || []), ...attachments]
            }));
        }

        // Remove from pending files
        const fileKeys = files.map(f => f.file_key);
        setPendingIntentFiles(prev => prev.filter(f => !fileKeys.includes(f.file_key)));

        // Update file status
        setAiUploadedFiles(prev => prev.map(f =>
            fileKeys.includes(f.file_key)
                ? { ...f, intent: 'attachment', processing_status: 'attached' }
                : f
        ));

        // Add confirmation message
        const fileNames = newToAttach.map(f => f.file_name).join(', ');
        if (newToAttach.length > 0) {
            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `📎 **Attached to notice:** ${fileNames}\n\nThe file will be available for employees to download when this notice is published.`,
                timestamp: new Date(),
                action: 'file_attached'
            };
            setMessages(prev => [...prev, confirmMessage]);
            toast.success(`📎 Attached: ${fileNames}`);
        }
    };

    // Handle "Let AI Analyze" intent choice
    const handleLetAIAnalyze = async (files) => {
        setFileProcessingMessage('Processing documents for AI analysis...');

        try {
            let alreadyAnalyzedCount = 0;
            let newlyAnalyzedCount = 0;

            for (const file of files) {
                // Call confirm-intent endpoint with the file buffer
                const response = await api.post('/files/ai-upload/confirm-intent', {
                    file_key: file.file_key,
                    session_id: sessionId,
                    intent: 'reference',
                    file_buffer_base64: file.file_buffer_base64
                });

                if (response.data.success) {
                    // Check if file was already analyzed
                    if (response.data.already_analyzed) {
                        alreadyAnalyzedCount++;
                        toast.success(`📄 "${file.file_name}" was already analyzed`, { duration: 3000 });
                    } else {
                        newlyAnalyzedCount++;
                    }

                    // Update file status
                    setAiUploadedFiles(prev => prev.map(f =>
                        f.file_key === file.file_key
                            ? {
                                ...f,
                                intent: 'reference',
                                embedding_status: response.data.embedding_status,
                                processing_status: response.data.embedding_status,
                                document_id: response.data.document_id
                            }
                            : f
                    ));
                }
            }

            // Remove from pending files
            const fileKeys = files.map(f => f.file_key);
            setPendingIntentFiles(prev => prev.filter(f => !fileKeys.includes(f.file_key)));

            // Add confirmation message based on analysis status
            const fileNames = files.map(f => f.file_name).join(', ');
            let messageContent;

            if (alreadyAnalyzedCount > 0 && newlyAnalyzedCount === 0) {
                messageContent = `📄 **${fileNames}** was already analyzed. I can answer questions about it, create notices based on it, or summarize it.\n\nWhat would you like me to do?`;
            } else {
                messageContent = `🧠 I've analyzed **${fileNames}**. I can now answer questions about it, create notices based on its content, or summarize it.\n\nWhat would you like me to do?`;
            }

            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: messageContent,
                timestamp: new Date(),
                action: 'file_processed'
            };
            setMessages(prev => [...prev, confirmMessage]);
            setSuggestions([
                'Summarize this document',
                'Create a notice from this content',
                'What are the key points?',
                'Create an announcement based on this'
            ]);

        } catch (error) {
            console.error('Error processing file for AI:', error);
            toast.error('Failed to process file for AI analysis');
        } finally {
            setFileProcessingMessage(null);
        }
    };

    // Handle removing an AI uploaded file
    const handleRemoveAiFile = async (file) => {
        try {
            // If it was processed as reference, delete from embeddings
            if (file.document_id) {
                await api.delete(`/files/ai-reference/${file.document_id}`);
            }
            setAiUploadedFiles(prev => prev.filter(f => f.file_key !== file.file_key));
            toast.success('File removed');
        } catch (error) {
            console.error('Error removing file:', error);
            // Still remove from local state
            setAiUploadedFiles(prev => prev.filter(f => f.file_key !== file.file_key));
        }
    };

    const handleClose = () => {
        if (hasUnsavedChanges && hasEditorContent()) {
            setShowUnsavedModal(true);
        } else {
            onClose();
        }
    };

    const handleSaveAsDraftFromModal = async () => {
        setShowUnsavedModal(false);
        await handleSave('draft');
    };

    const handleLeaveWithoutSaving = () => {
        setShowUnsavedModal(false);
        localStorage.removeItem('ai_notice_draft_session');
        onClose();
    };

    const handleSave = async (status = 'draft') => {
        if (!formData.title.trim()) {
            toast.error('Please enter a notice title');
            return;
        }

        if (!formData.content.trim()) {
            toast.error('Please enter notice content');
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                status,
                ai_chat_history: messages,
                scheduled_date: formData.scheduled_date || null,
                expiry_date: formData.expiry_date || null,
                external_link: formData.external_link || null
            };

            let savedNotice;

            if (isEditMode) {
                const res = await api.put(`/notices/${editNotice.id}`, payload);
                savedNotice = res.data?.notice;
                toast.success('Notice updated successfully!');
            } else {
                const res = await api.post('/notices', payload);
                savedNotice = res.data?.notice;
                toast.success(isReminderMode ? 'Reminder notice sent!' : 'Notice created successfully!');
            }

            // If this was a reminder, record it in the activity_reminders table
            if (isReminderMode && status === 'published') {
                try {
                    const token = localStorage.getItem('token');
                    await api.post('/reminders/send', {
                        activity_type: prefilledData.activityType,
                        activity_id: prefilledData.activityId,
                        reminder_type: 'manual',
                        notice_id: savedNotice?.id || null,
                        recipient_count: formData.assigned_employee_ids?.length || 0,
                        recipient_ids: formData.assigned_employee_ids || [],
                        content_preview: formData.title
                    }, { headers: { Authorization: `Bearer ${token}` } });
                } catch (reminderErr) {
                    console.error('Failed to record reminder:', reminderErr);
                    // Don't fail the save over this
                }
            }

            // Clear draft session
            localStorage.removeItem('ai_notice_draft_session');
            onSuccess();
        } catch (error) {
            console.error('Error saving notice:', error);
            toast.error('Failed to save notice');
        } finally {
            setIsSaving(false);
        }
    };

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'urgent':
                return <AlertTriangle className="w-4 h-4" />;
            case 'pinned':
                return <Pin className="w-4 h-4" />;
            default:
                return <Bell className="w-4 h-4" />;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex flex-col bg-slate-900">
            {/* Header */}
            <div className="flex-shrink-0 bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white">
                            {isEditMode ? 'Edit Notice with AI' : 'AI Notice Generator'}
                        </h2>
                        <p className="text-white/80 text-sm capitalize">{phase} Phase</p>
                    </div>

                    {/* AI Chat Toggle — left side, with label */}
                    <button
                        onClick={toggleChatCollapsed}
                        className="hidden lg:flex items-center gap-2 ml-3 px-3 py-1.5 rounded-full transition-all duration-200 bg-white/15 hover:bg-white/25 border border-white/20 hover:border-white/40"
                        title={isChatCollapsed ? 'Show AI Chat Panel' : 'Hide AI Chat Panel'}
                    >
                        {isChatCollapsed ? (
                            <>
                                <Bot className="w-4 h-4 text-white" />
                                <span className="text-xs font-semibold text-white whitespace-nowrap">Show AI Chat</span>
                            </>
                        ) : (
                            <>
                                <PanelLeftClose className="w-4 h-4 text-white" />
                                <span className="text-xs font-semibold text-white/80 whitespace-nowrap">Hide Chat</span>
                            </>
                        )}
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>
            </div>

            {/* Mobile Tab Switcher */}
            <div className="lg:hidden flex border-b border-slate-700 bg-slate-800">
                <button
                    onClick={() => setMobileActivePanel('chat')}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${mobileActivePanel === 'chat'
                        ? 'text-white border-b-2 border-violet-500'
                        : 'text-slate-400'
                        }`}
                >
                    <Bot className="w-4 h-4 inline mr-2" />
                    AI Chat
                </button>
                <button
                    onClick={() => setMobileActivePanel('editor')}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${mobileActivePanel === 'editor'
                        ? 'text-white border-b-2 border-violet-500'
                        : 'text-slate-400'
                        }`}
                >
                    <Bell className="w-4 h-4 inline mr-2" />
                    Notice Editor
                </button>
            </div>

            {/* Main Content */}
            <div ref={containerRef} className="flex-1 flex overflow-hidden">
                {/* Collapsed Chat Sidebar Strip (desktop only) */}
                {isChatCollapsed && (
                    <div
                        className="hidden lg:flex flex-col items-center bg-slate-800 border-r border-slate-700 cursor-pointer hover:bg-slate-750 transition-colors group"
                        style={{ width: '48px', minWidth: '48px' }}
                        onClick={toggleChatCollapsed}
                        title="Expand AI Chat"
                    >
                        <div className="flex flex-col items-center gap-3 py-5">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20 group-hover:scale-110 transition-transform">
                                <Bot className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-[10px] font-bold text-slate-400 tracking-widest uppercase" style={{ writingMode: 'vertical-lr' }}>AI Chat</span>
                            </div>
                        </div>
                        <div className="mt-auto mb-4 p-1.5 rounded-lg bg-slate-700/50 group-hover:bg-violet-600/30 transition-colors">
                            <PanelLeftOpen className="w-4 h-4 text-slate-400 group-hover:text-violet-300 transition-colors" />
                        </div>
                    </div>
                )}

                {/* Chat Panel */}
                <div
                    className={`flex flex-col bg-slate-800 ${mobileActivePanel === 'chat' ? 'flex' : 'hidden lg:flex'}`}
                    style={{
                        width: window.innerWidth >= 1024
                            ? (isChatCollapsed ? '0px' : `${splitRatio}%`)
                            : '100%',
                        minWidth: window.innerWidth >= 1024 && isChatCollapsed ? '0px' : undefined,
                        overflow: isChatCollapsed && window.innerWidth >= 1024 ? 'hidden' : undefined,
                        opacity: isChatCollapsed && window.innerWidth >= 1024 ? 0 : 1,
                        transition: 'width 300ms ease, opacity 200ms ease, min-width 300ms ease',
                        pointerEvents: isChatCollapsed && window.innerWidth >= 1024 ? 'none' : undefined
                    }}
                >
                    <div className="flex justify-between items-center px-4 py-2 border-b border-slate-700/50 lg:hidden">
                        <span className="text-xs font-semibold text-slate-400">AI Chat</span>
                    </div>
                    {/* Chat Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                            >
                                {msg.role === 'assistant' && (
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                        <Bot className="w-4 h-4 text-white" />
                                    </div>
                                )}
                                <div
                                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.role === 'user'
                                        ? 'bg-violet-600 text-white'
                                        : 'bg-slate-700 text-slate-100'
                                        }`}
                                >
                                    {/* Message Content */}
                                    <div className="text-[15px] text-slate-200 leading-relaxed">
                                        {msg.content.split('\n').map((line, i) => (
                                            <p key={i} className="mb-1.5 last:mb-0">
                                                {line.startsWith('**') && line.endsWith('**') ? (
                                                    <strong className="text-white">{line.slice(2, -2)}</strong>
                                                ) : line.startsWith('- ') ? (
                                                    <span className="flex gap-2 text-slate-300">
                                                        <span className="text-violet-400">•</span>
                                                        {line.slice(2)}
                                                    </span>
                                                ) : line}
                                            </p>
                                        ))}
                                    </div>

                                    {/* Web Search Results */}
                                    {msg.webSearchResults && msg.webSearchResults.length > 0 && (
                                        <WebSearchResultsDisplay results={msg.webSearchResults} />
                                    )}

                                    {/* Intent Chooser Buttons */}
                                    {msg.action === 'file_intent_choice' && msg.pendingFiles && (
                                        <div className="mt-3 pt-3 border-t border-slate-700/30">
                                            <div className="flex flex-wrap gap-2">
                                                <button
                                                    onClick={() => handleAttachToNotice(msg.pendingFiles)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg font-medium transition-all text-sm border border-slate-600/50"
                                                >
                                                    <Paperclip className="w-4 h-4 text-blue-400" />
                                                    <span>Attach to Notice</span>
                                                </button>
                                                <button
                                                    onClick={() => handleLetAIAnalyze(msg.pendingFiles)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg font-medium transition-all text-sm shadow-lg shadow-violet-500/20"
                                                >
                                                    <Brain className="w-4 h-4" />
                                                    <span>Let AI Read & Analyze</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Timestamp */}
                                    <p className="text-[11px] text-slate-500 mt-2">
                                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                                {msg.role === 'user' && (
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center">
                                        <User className="w-4 h-4 text-white" />
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Streaming Message */}
                        {isStreaming && (
                            <div className="flex gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                    <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-slate-700 text-slate-100">
                                    {streamingPhase && (
                                        <div className="flex items-center gap-2 text-xs text-violet-400 mb-2">
                                            <Loader className="w-3 h-3 animate-spin" />
                                            {streamingPhase}
                                        </div>
                                    )}
                                    {streamingMessage && (
                                        <div className="text-sm whitespace-pre-wrap">{streamingMessage}</div>
                                    )}
                                    {!streamingMessage && !streamingPhase && (
                                        <div className="flex gap-1">
                                            <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                            <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                            <span className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* File Processing Indicator */}
                        {fileProcessingMessage && (
                            <div className="flex gap-3">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                    <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-slate-700 text-slate-100">
                                    <div className="flex items-center gap-2 text-xs text-violet-400">
                                        <Loader className="w-3 h-3 animate-spin" />
                                        {fileProcessingMessage}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div ref={chatEndRef} />
                    </div>

                    {/* Suggestions */}
                    {suggestions.length > 0 && !isGenerating && (
                        <div className="px-4 pb-2">
                            <div className="flex items-center gap-2 mb-2">
                                <Lightbulb className="w-4 h-4 text-amber-400" />
                                <span className="text-xs text-slate-400">Suggestions</span>
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-2">
                                {suggestions.map((suggestion, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        className="flex-shrink-0 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-full transition-colors"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Uploaded Files Display */}
                    {aiUploadedFiles.length > 0 && (
                        <div className="px-4 pb-2 flex gap-2 flex-wrap">
                            {aiUploadedFiles.map((file, idx) => (
                                <div key={idx} className="flex items-center gap-2 px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">
                                    <File className="w-3 h-3" />
                                    <span className="truncate max-w-[100px]">{file.file_name}</span>
                                    <button
                                        onClick={() => setAiUploadedFiles(prev => prev.filter((_, i) => i !== idx))}
                                        className="text-slate-400 hover:text-red-400"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Chat Input */}
                    <div className="p-4 border-t border-slate-700">
                        <div className="flex gap-2">
                            <div className="flex-1 relative">
                                {/* Hidden File Input for Claude/Gemini-style inline file picker */}
                                <input
                                    ref={aiFileInputRef}
                                    type="file"
                                    multiple
                                    accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                                    onChange={handleDirectFileUpload}
                                    disabled={isGenerating || isFileUploading || aiUploadedFiles.length >= 5}
                                    className="hidden"
                                />

                                {/* Inline File Upload Progress */}
                                {isFileUploading && (
                                    <div className="mb-2 bg-slate-700/50 rounded-lg p-2">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Loader className="w-3 h-3 animate-spin text-violet-400" />
                                            <span className="text-xs text-slate-300">Uploading...</span>
                                            <span className="text-xs text-slate-500 ml-auto">{fileUploadProgress}%</span>
                                        </div>
                                        <div className="w-full bg-slate-600 rounded-full h-1 overflow-hidden">
                                            <div
                                                className="bg-gradient-to-r from-violet-500 to-purple-500 h-full rounded-full transition-all duration-300"
                                                style={{ width: `${fileUploadProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Uploaded Files Display - Claude/Gemini style compact chips */}
                                {aiUploadedFiles.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {aiUploadedFiles.map((file) => {
                                            const ext = file.file_name?.split('.').pop()?.toUpperCase() || 'FILE';
                                            const isPdf = ext === 'PDF';
                                            const isDoc = ['DOC', 'DOCX'].includes(ext);
                                            const isImage = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(ext);
                                            const isSpreadsheet = ['XLS', 'XLSX'].includes(ext);

                                            return (
                                                <div
                                                    key={file.file_key}
                                                    className="group flex items-center gap-1.5 bg-slate-700/60 hover:bg-slate-700/80 border border-slate-600/50 rounded-md px-2 py-1 transition-colors"
                                                >
                                                    <div className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${isPdf ? 'bg-red-500/20 text-red-400' :
                                                        isDoc ? 'bg-blue-500/20 text-blue-400' :
                                                            isImage ? 'bg-green-500/20 text-green-400' :
                                                                isSpreadsheet ? 'bg-emerald-500/20 text-emerald-400' :
                                                                    'bg-slate-600/50 text-slate-400'
                                                        }`}>
                                                        {isPdf ? 'PDF' : isDoc ? 'DOC' : isImage ? 'IMG' : isSpreadsheet ? 'XLS' : ext.slice(0, 3)}
                                                    </div>
                                                    <span className="text-xs text-slate-300 max-w-[100px] truncate">
                                                        {file.file_name}
                                                    </span>
                                                    <button
                                                        onClick={() => handleRemoveAiFile(file)}
                                                        className="p-0.5 text-slate-500 hover:text-red-400 rounded transition-colors opacity-60 group-hover:opacity-100"
                                                        title="Remove file"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <textarea
                                    ref={inputRef}
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Describe the notice you want to create..."
                                    className="w-full px-4 py-3 pr-24 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                                    rows={2}
                                    disabled={isGenerating}
                                />
                                <div className="absolute right-2 bottom-2 flex gap-1">
                                    {/* Paperclip now directly triggers file picker like Claude/Gemini */}
                                    <button
                                        onClick={() => aiFileInputRef.current?.click()}
                                        disabled={isGenerating || isFileUploading || aiUploadedFiles.length >= 5}
                                        className={`p-2 rounded-lg transition-colors ${aiUploadedFiles.length > 0
                                            ? 'bg-violet-600 text-white'
                                            : 'text-slate-400 hover:text-white hover:bg-slate-600'
                                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                                        title={aiUploadedFiles.length >= 5 ? 'Maximum 5 files' : `Attach files (${aiUploadedFiles.length}/5)`}
                                    >
                                        <Paperclip className="w-4 h-4" />
                                    </button>
                                    {/* <button
                                        onClick={() => setIsWebSearchEnabled(!isWebSearchEnabled)}
                                        className={`p-2 rounded-lg transition-colors ${isWebSearchEnabled ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-600'
                                            }`}
                                        title="Web search"
                                    >
                                        <Globe className="w-4 h-4" />
                                    </button> */}
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={!inputMessage.trim() || isGenerating}
                                        className="p-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {isGenerating ? (
                                            <Loader className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Send className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Resize Handle */}
                {!isChatCollapsed && (
                    <div
                        className="hidden lg:flex w-1 cursor-col-resize bg-slate-700 hover:bg-violet-500 transition-colors items-center justify-center group"
                        onMouseDown={handleMouseDown}
                    >
                        <div className="w-1 h-8 bg-slate-500 group-hover:bg-violet-400 rounded-full" />
                    </div>
                )}

                {/* Editor Panel */}
                <div
                    className={`flex-1 flex flex-col bg-white overflow-hidden ${mobileActivePanel === 'editor' ? 'flex' : 'hidden lg:flex'} relative transition-all duration-300`}
                    style={{
                        width: window.innerWidth >= 1024
                            ? (isChatCollapsed ? '100%' : `${100 - splitRatio}%`)
                            : '100%',
                        flex: isChatCollapsed && window.innerWidth >= 1024 ? '1 1 0%' : undefined,
                        transition: 'width 300ms ease, flex 300ms ease'
                    }}
                >
                    {/* AI Loading Overlay */}
                    {isGenerating && (
                        <div className="absolute inset-0 z-20 bg-white/50 backdrop-blur-[1px] flex flex-col items-center justify-center animate-in fade-in duration-300">
                            <div className="bg-white p-6 rounded-2xl shadow-xl border border-violet-100 flex flex-col items-center gap-4 max-w-sm mx-4 text-center">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-violet-500/20 rounded-full blur-xl animate-pulse" />
                                    <div className="relative bg-gradient-to-tr from-violet-600 to-purple-600 p-4 rounded-full shadow-lg">
                                        <Loader2 className="w-8 h-8 text-white animate-spin" />
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <h3 className="font-semibold text-gray-900">Generating Notice...</h3>
                                    <p className="text-sm text-gray-500">AI is crafting your content based on your request.</p>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Editor Tabs */}
                    <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200 bg-gray-50">
                        <div className="flex gap-2">
                            {['content', 'targeting', 'settings'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveEditorTab(tab)}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeEditorTab === tab
                                        ? 'bg-violet-600 text-white'
                                        : 'text-gray-600 hover:bg-gray-200'
                                        }`}
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Editor Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {/* Content Tab */}
                        {activeEditorTab === 'content' && (
                            <div className="space-y-6">
                                {/* Title */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Notice Title *
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Enter notice title..."
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Priority */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Priority
                                    </label>
                                    <div className="flex gap-3">
                                        {[
                                            { value: 'normal', label: 'Normal', icon: Bell, color: 'blue' },
                                            { value: 'pinned', label: 'Pinned', icon: Pin, color: 'amber' },
                                            { value: 'urgent', label: 'Urgent', icon: AlertTriangle, color: 'red' }
                                        ].map(({ value, label, icon: Icon, color }) => (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => setFormData(prev => ({ ...prev, priority: value }))}
                                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${formData.priority === value
                                                    ? 'border-violet-500 bg-violet-50 text-violet-700'
                                                    : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                <span className="font-medium">{label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Content Editor */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Notice Content *
                                    </label>
                                    <RichTextEditor
                                        value={formData.content}
                                        onChange={handleContentChange}
                                        placeholder="Write your notice content here..."
                                    />
                                </div>

                                {/* External Link */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <div className="flex items-center gap-2">
                                            <LinkIcon className="w-4 h-4" />
                                            External Link (Optional)
                                        </div>
                                    </label>
                                    <input
                                        type="url"
                                        name="external_link"
                                        value={formData.external_link}
                                        onChange={handleInputChange}
                                        placeholder="https://example.com"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    />
                                </div>

                                {/* Attachments Section */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Paperclip className="w-4 h-4" />
                                                Attachments
                                            </div>
                                            <span className="text-xs text-gray-500">
                                                {formData.attachments.length} file(s)
                                            </span>
                                        </div>
                                    </label>

                                    {/* Existing Attachments */}
                                    {formData.attachments.length > 0 && (
                                        <div className="space-y-2 mb-3">
                                            {formData.attachments.map((file, index) => (
                                                <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                                                    <div className="flex items-center gap-3">
                                                        <div className="p-2 bg-violet-100 rounded-lg">
                                                            <File className="w-4 h-4 text-violet-600" />
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-700 truncate max-w-[200px] block">
                                                                {file.file_name}
                                                            </span>
                                                            {file.file_size && (
                                                                <span className="text-xs text-gray-500">
                                                                    {(file.file_size / 1024).toFixed(1)} KB
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveAttachment(index)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Upload Button */}
                                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-violet-400 hover:bg-violet-50/50 transition-colors">
                                        <p className="text-sm text-gray-600 mb-2">
                                            Upload files using the chat panel on the left
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Click <strong>Attach files</strong> button → Select <strong>Attach to Notice</strong>
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setMobileActivePanel('chat');
                                                aiFileInputRef.current?.click();
                                            }}
                                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg transition-colors"
                                        >
                                            <Upload className="w-4 h-4" />
                                            Upload File
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Targeting Tab */}
                        {activeEditorTab === 'targeting' && (
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Who should see this notice?
                                    </label>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        {[
                                            { value: 'all', label: 'Company-Wide', desc: 'All employees', icon: Users },
                                            { value: 'role', label: 'Role-Specific', desc: 'By position', icon: Users },
                                            { value: 'individual', label: 'Specific People', desc: 'Select individuals', icon: User },
                                            { value: 'placeholder', label: 'Placeholder', desc: 'Manager, buddy, etc.', icon: Users }
                                        ].map(({ value, label, desc, icon: Icon }) => (
                                            <button
                                                key={value}
                                                type="button"
                                                onClick={() => setFormData(prev => ({
                                                    ...prev,
                                                    assignment_type: value,
                                                    assigned_roles: value !== 'role' ? [] : prev.assigned_roles,
                                                    assigned_employee_ids: value !== 'individual' ? [] : prev.assigned_employee_ids,
                                                    assignment_placeholders: value !== 'placeholder' ? [] : prev.assignment_placeholders
                                                }))}
                                                className={`p-4 rounded-xl border-2 text-left transition-all ${formData.assignment_type === value
                                                    ? 'border-violet-500 bg-violet-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                            >
                                                <Icon className={`w-5 h-5 mb-2 ${formData.assignment_type === value ? 'text-violet-600' : 'text-gray-400'
                                                    }`} />
                                                <p className={`font-medium text-sm ${formData.assignment_type === value ? 'text-violet-700' : 'text-gray-700'
                                                    }`}>{label}</p>
                                                <p className="text-xs text-gray-500 mt-1">{desc}</p>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Placeholder Assignment Picker */}
                                {formData.assignment_type === 'placeholder' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-3">
                                            Select Assignment Placeholders
                                        </label>
                                        <AssignmentPlaceholderPicker
                                            value={formData.assignment_placeholders || []}
                                            onChange={(val) => setFormData(prev => ({
                                                ...prev,
                                                assignment_placeholders: val
                                            }))}
                                        />
                                    </div>
                                )}

                                {formData.assignment_type === 'role' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-3">
                                            Select Roles
                                        </label>
                                        {availableRoles.length === 0 ? (
                                            <p className="text-gray-500 text-sm">No roles found.</p>
                                        ) : (
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                {availableRoles.map((role) => (
                                                    <button
                                                        key={role}
                                                        type="button"
                                                        onClick={() => handleRoleToggle(role)}
                                                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${formData.assigned_roles.includes(role)
                                                            ? 'border-violet-500 bg-violet-50 text-violet-700'
                                                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                                            }`}
                                                    >
                                                        {role}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {formData.assignment_type === 'individual' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-3">
                                            Select Employees ({formData.assigned_employee_ids.length} selected)
                                        </label>
                                        {employees.length === 0 ? (
                                            <p className="text-gray-500 text-sm">No employees found.</p>
                                        ) : (
                                            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-xl">
                                                {employees.map((employee) => (
                                                    <button
                                                        key={employee.id}
                                                        type="button"
                                                        onClick={() => handleEmployeeToggle(employee.id)}
                                                        className={`w-full flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0 transition-colors ${formData.assigned_employee_ids.includes(employee.id)
                                                            ? 'bg-violet-50'
                                                            : 'hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${formData.assigned_employee_ids.includes(employee.id)
                                                            ? 'border-violet-500 bg-violet-500'
                                                            : 'border-gray-300'
                                                            }`}>
                                                            {formData.assigned_employee_ids.includes(employee.id) && (
                                                                <Check className="w-3 h-3 text-white" />
                                                            )}
                                                        </div>
                                                        <div className="flex-1 text-left">
                                                            <p className="font-medium text-gray-800">
                                                                {employee.first_name} {employee.last_name}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {employee.position || 'No position'} • {employee.email}
                                                            </p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Settings Tab */}
                        {activeEditorTab === 'settings' && (
                            <div className="space-y-6">
                                {/* Popup Setting */}
                                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-200">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <Eye className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-800">Show as Popup on Login</p>
                                            <p className="text-sm text-gray-600">Display this notice as a popup when employees log in</p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="show_popup_on_login"
                                            checked={formData.show_popup_on_login}
                                            onChange={handleInputChange}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                    </label>
                                </div>

                                {/* Scheduled Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            Schedule for Later (Optional)
                                        </div>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="scheduled_date"
                                        value={formData.scheduled_date}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Leave empty to publish immediately</p>
                                </div>

                                {/* Expiry Date */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <div className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4" />
                                            Expiry Date (Optional)
                                        </div>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="expiry_date"
                                        value={formData.expiry_date}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Notice will automatically hide after this date</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Editor Footer */}
                    <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <div className="flex gap-3">
                            <button
                                onClick={() => handleSave('draft')}
                                disabled={isSaving}
                                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors disabled:opacity-50"
                            >
                                Save as Draft
                            </button>
                            {formData.scheduled_date ? (
                                <button
                                    onClick={() => handleSave('scheduled')}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                    <Clock className="w-4 h-4" />
                                    Schedule
                                </button>
                            ) : (
                                <button
                                    onClick={() => handleSave('published')}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                    {isSaving ? (
                                        <Loader className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Bell className="w-4 h-4" />
                                    )}
                                    Publish Now
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Unsaved Changes Modal */}
            {showUnsavedModal && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full mx-auto overflow-hidden ring-1 ring-black/5 transform transition-all scale-100 opacity-100">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-6 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-xl"></div>
                            <div className="relative flex items-center gap-4">
                                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm shadow-inner">
                                    <AlertTriangle className="w-7 h-7 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-white tracking-tight">Unsaved Changes</h3>
                                    <p className="text-amber-100 font-medium text-sm">You have unsaved work in progress</p>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="px-8 py-8">
                            <p className="text-gray-600 text-lg leading-relaxed mb-6">
                                You have modified this notice but haven't saved your changes yet. If you leave now, your progress will be lost.
                            </p>

                            {/* Notice Preview */}
                            {formData.title && (
                                <div className="p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 shadow-sm relative overflow-hidden group hover:border-violet-200 transition-colors">
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-gray-100 rounded-full -mr-12 -mt-12 opacity-50"></div>
                                    <div className="flex items-start gap-4 relative">
                                        <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600 shadow-sm group-hover:scale-110 transition-transform duration-300">
                                            <Bell className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-bold text-gray-900 truncate text-lg pr-4">{formData.title}</h4>
                                            <div className="flex items-center gap-3 mt-1.5">
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wide">
                                                    {formData.phase}
                                                </span>
                                                <span className="text-sm text-gray-500 font-medium capitalize">
                                                    {formData.priority} priority
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!formData.title?.trim() && (
                                <div className="p-4 bg-amber-50 text-amber-800 rounded-xl border border-amber-100 text-sm font-medium flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    Notice title is required to save as draft
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="px-8 py-6 bg-gray-50/80 border-t border-gray-100 flex flex-col-reverse sm:flex-row gap-4">
                            <button
                                onClick={handleLeaveWithoutSaving}
                                className="flex-1 px-6 py-3.5 text-gray-600 bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 border border-gray-200 rounded-xl font-semibold transition-all duration-200 shadow-sm focus:ring-2 focus:ring-red-100 outline-none"
                            >
                                Leave Without Saving
                            </button>
                            <button
                                onClick={handleSaveAsDraftFromModal}
                                disabled={isSaving || !formData.title?.trim()}
                                className="flex-[1.5] px-6 py-3.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 transform hover:-translate-y-0.5 focus:ring-2 focus:ring-violet-200 outline-none"
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Saving Draft...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" />
                                        Save Changes & Leave
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AINoticeGenerator;
